import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import CommentsApi from 'app/modules/comments/api';
import { MentionableUser } from 'types/User';
import {
  ALLOWED_ROLES_TO_MENTION_IN_CLIENT_COMMENTS,
  ALLOWED_ROLES_TO_MENTION_IN_INTERNAL_COMMENTS,
} from 'constants/comments';
import { useMemo } from 'react';
import { PostCommunicationContent } from 'app/modules/posts/modules/post/components/postCommunication/types/postCommunicationContent';

type Props = {
  type: PostCommunicationContent;
  postId: number;
};

export function useMentionableUsers({ postId, type }: Props) {
  const mentionableUsers = useQuery(
    queryKey.commentsForPostMentionableUsers(postId),
    () => CommentsApi.mentionableUsers({ postId }),
  );

  const resolvable = useMemo(
    (): MentionableUser[] => mentionableUsers.data ?? [],
    [mentionableUsers.data],
  );

  const data = useMemo((): MentionableUser[] => {
    const allowedRoles =
      type === PostCommunicationContent.CLIENT
        ? ALLOWED_ROLES_TO_MENTION_IN_CLIENT_COMMENTS
        : ALLOWED_ROLES_TO_MENTION_IN_INTERNAL_COMMENTS;

    return resolvable.filter((user) => allowedRoles.includes(user.role));
  }, [resolvable, type]);

  return useMemo(
    () => ({
      data,
      resolvable,
      isLoading: mentionableUsers.isLoading,
    }),
    [mentionableUsers.isLoading, data, resolvable],
  );
}
